const STORAGE_KEY = 'niamia-wishlist';

/**
 * @typedef {{ handle: string, id?: string }} WishlistItem
 */

/**
 * @returns {WishlistItem[]}
 */
function readList() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item.handle === 'string' && item.handle.length > 0);
  } catch {
    return [];
  }
}

/**
 * @param {WishlistItem[]} items
 */
function saveList(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  syncWishlist();
}

const WISHLIST_URL = '/pages/contact?view=wishlist';

const HEART_SVG =
  '<span class="svg-wrapper" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 20 20" fill="none"><path d="M10 16.85 3.85 11.2a4.15 4.15 0 0 1 5.72-6.02L10 5.9l.43-.72a4.15 4.15 0 0 1 5.72 6.02L10 16.85Z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round" fill="none"/></svg></span>';

const TRASH_SVG =
  '<svg class="wishlist-trash-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trash preview-icon"><path d="M10 11v6"/><path d="M14 11v6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

function rewriteWishlistLinks() {
  document.querySelectorAll('a[href*="/pages/wishlist"]').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = new URL(link.href, window.location.origin);
    if (url.pathname.replace(/\/$/, '').endsWith('/pages/wishlist')) {
      link.href = WISHLIST_URL;
    }
  });
}

function syncWishlist() {
  rewriteWishlistLinks();
  const items = readList();
  const handles = new Set(items.map((item) => item.handle));
  const count = String(items.length);

  document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
    if (badge.textContent !== count) badge.textContent = count;
  });

  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const saved = handles.has(button.dataset.productHandle || '');
    const pressed = saved ? 'true' : 'false';
    if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    const label = saved ? button.dataset.removeLabel : button.dataset.addLabel;
    if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  });
}

/**
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const amount = (Number(cents) / 100).toFixed(2);
  const [whole, fraction] = amount.split('.');
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const value = `${withCommas}.${fraction}`;
  const formatted = (format || 'Rs.{{amount}}')
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
    .replace(/\{\{\s*amount\s*\}\}/g, value);
  return formatted.includes('{{') ? `Rs.${value}` : formatted;
}

/**
 * @param {HTMLElement} page
 * @param {object} product
 * @param {WishlistItem} item
 * @param {string} root
 * @param {string} moneyFormat
 * @param {string} removeLabel
 */
function createCollectionStyleCard(page, product, item, root, moneyFormat, removeLabel) {
  const productUrl = `${root}products/${product.handle}`;
  const title = product.title || item.handle;

  const li = document.createElement('li');
  li.className = 'product-grid__item';
  li.dataset.productId = String(product.id || item.id || '');
  li.dataset.productHandle = product.handle;

  const card = document.createElement('div');
  card.className = 'product-card';
  card.dataset.productId = String(product.id || '');

  const overlayLink = document.createElement('a');
  overlayLink.className = 'product-card__link';
  overlayLink.href = productUrl;
  overlayLink.setAttribute('aria-label', title);

  const content = document.createElement('div');
  content.className =
    'product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style';
  content.style.setProperty('--product-card-gap', '8px');
  content.style.setProperty('--flex-direction', 'column');

  const gallery = document.createElement('div');
  gallery.className = 'card-gallery';
  gallery.style.setProperty('--gallery-aspect-ratio', '4 / 5');
  gallery.dataset.imageRatio = 'portrait';

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'product-card__wishlist product-card__trash';
  remove.dataset.wishlistToggle = '';
  remove.dataset.productHandle = product.handle;
  remove.dataset.productId = String(product.id || item.id || '');
  remove.dataset.addLabel = page.dataset.addLabel || 'Add to wishlist';
  remove.dataset.removeLabel = removeLabel;
  remove.setAttribute('aria-pressed', 'true');
  remove.setAttribute('aria-label', removeLabel);
  remove.style.cssText = 'top: 8px; left: 8px;';
  remove.innerHTML = TRASH_SVG;
  gallery.append(remove);

  const imageSrc = typeof product.featured_image === 'string' ? product.featured_image : product.images?.[0] || '';
  if (imageSrc) {
    const imageLink = document.createElement('a');
    imageLink.className = 'contents';
    imageLink.href = productUrl;
    imageLink.setAttribute('aria-label', title);

    const image = document.createElement('img');
    image.className = 'product-media__image';
    image.alt = title;
    image.width = 600;
    image.height = 750;
    image.loading = 'lazy';
    image.src = imageSrc.includes('?') ? `${imageSrc}&width=600` : `${imageSrc}?width=600`;
    imageLink.append(image);
    gallery.append(imageLink);
  }

  const sizes = getProductSizes(product);
  if (sizes.length) {
    const sizesEl = document.createElement('div');
    sizesEl.className = 'product-card__sizes';
    sizes.slice(0, 5).forEach((size) => {
      const span = document.createElement('span');
      span.className = 'product-card__size-item';
      span.textContent = size;
      sizesEl.append(span);
    });
    if (sizes.length > 5) {
      const more = document.createElement('span');
      more.className = 'product-card__size-item';
      more.textContent = `+${sizes.length - 5}`;
      sizesEl.append(more);
    }
    gallery.append(sizesEl);
  }

  const meta = document.createElement('div');
  meta.className = 'wishlist-card__meta group-block';

  const titleLink = document.createElement('a');
  titleLink.className = 'wishlist-card__title text-block';
  titleLink.href = productUrl;
  titleLink.textContent = title;

  const price = document.createElement('p');
  price.className = 'wishlist-card__price price text-block';
  price.textContent = formatMoney(product.price, moneyFormat);

  meta.append(titleLink, price);
  content.append(gallery, meta);
  card.append(overlayLink, content);
  li.append(card);
  return li;
}

/**
 * @param {object} product
 * @returns {string[]}
 */
function getProductSizes(product) {
  const options = Array.isArray(product.options) ? product.options : [];
  const sizeIndex = options.findIndex((name) => typeof name === 'string' && name.toLowerCase().includes('size'));
  if (sizeIndex < 0 || !Array.isArray(product.variants)) return [];

  const key = /** @type {'option1' | 'option2' | 'option3'} */ (`option${sizeIndex + 1}`);
  const values = [];
  const seen = new Set();
  for (const variant of product.variants) {
    const value = variant?.[key];
    if (typeof value !== 'string' || !value || seen.has(value)) continue;
    seen.add(value);
    values.push(value);
  }
  return values;
}

/**
 * @param {HTMLElement} page
 * @param {boolean} hasItems
 */
function setToolbarVisibility(page, hasItems) {
  const toolbar = page.querySelector('[data-wishlist-toolbar]');
  if (toolbar instanceof HTMLElement) toolbar.hidden = !hasItems;
}

/**
 * @param {HTMLElement} page
 */
function bindDensityControls(page) {
  if (page.dataset.densityBound === 'true') return;
  page.dataset.densityBound = 'true';

  const grid = page.querySelector('[data-wishlist-grid]');
  if (!(grid instanceof HTMLElement)) return;

  page.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'grid' && target.name !== 'grid-mobile') return;

    const value = target.value;
    grid.setAttribute('product-grid-view', value);

    const viewport = window.innerWidth >= 750 ? 'desktop' : 'mobile';
    try {
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    } catch {
      /* ignore quota / private mode */
    }
  });
}

/**
 * @param {HTMLElement} page
 */
async function renderWishlistPage(page) {
  const grid = page.querySelector('[data-wishlist-grid]');
  const gridWrap = page.querySelector('.wishlist-page__grid-wrap');
  const empty = page.querySelector('[data-wishlist-empty]');
  const status = page.querySelector('[data-wishlist-status]');
  if (!(grid instanceof HTMLElement) || !(empty instanceof HTMLElement)) return;

  bindDensityControls(page);

  const items = readList();
  console.log('Wishlist items:', items.length);
  
  // Clear grid first
  grid.replaceChildren();

  if (!items.length) {
    // Show empty state when no items
    empty.hidden = false;
    if (gridWrap instanceof HTMLElement) gridWrap.hidden = true;
    setToolbarVisibility(page, false);
    if (status instanceof HTMLElement) status.hidden = true;
    console.log('Showing empty state - no items');
    return;
  }

  // Hide empty state when items exist
  empty.hidden = true;
  if (gridWrap instanceof HTMLElement) gridWrap.hidden = false;
  setToolbarVisibility(page, true);
  if (status instanceof HTMLElement) status.hidden = false;
  console.log('Loading products for', items.length, 'items');

  const root = (page.dataset.root || '/').replace(/\/?$/, '/');
  const moneyFormat = page.dataset.moneyFormat || '';
  const removeLabel = page.dataset.removeLabel || 'Remove from wishlist';

  const products = await Promise.all(
    items.map(async (item) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, {
          credentials: 'same-origin',
        });
        if (!response.ok) return null;
        return await response.json();
      } catch {
        return null;
      }
    })
  );

  if (!page.isConnected) return;

  const kept = [];
  grid.replaceChildren();

  items.forEach((item, index) => {
    const product = products[index];
    if (!product) return;
    kept.push(item);
    grid.append(createCollectionStyleCard(page, product, item, root, moneyFormat, removeLabel));
  });

  if (status instanceof HTMLElement) status.hidden = true;

  if (kept.length !== items.length) saveList(kept);
  
  console.log('Products loaded:', kept.length);
  
  if (!kept.length) {
    // If all products failed to load, show empty
    empty.hidden = false;
    if (gridWrap instanceof HTMLElement) gridWrap.hidden = true;
    setToolbarVisibility(page, false);
    console.log('All products failed to load - showing empty');
  } else {
    // Products loaded successfully
    empty.hidden = true;
    if (gridWrap instanceof HTMLElement) gridWrap.hidden = false;
    console.log('Showing', kept.length, 'products');
  }

  syncWishlist();
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-wishlist-toggle]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();

  const handle = button.dataset.productHandle;
  if (!handle) return;

  const items = readList();
  const index = items.findIndex((item) => item.handle === handle);
  if (index >= 0) items.splice(index, 1);
  else items.push({ handle, id: button.dataset.productId || '' });
  saveList(items);

  const page = document.querySelector('[data-wishlist-page]');
  if (page instanceof HTMLElement && page.contains(button)) renderWishlistPage(page);
});

let syncFrame = 0;
const listObserver = new MutationObserver(() => {
  cancelAnimationFrame(syncFrame);
  syncFrame = requestAnimationFrame(syncWishlist);
});

if (document.body) listObserver.observe(document.body, { childList: true, subtree: true });

syncWishlist();

const wishlistPage = document.querySelector('[data-wishlist-page]');
if (wishlistPage instanceof HTMLElement) renderWishlistPage(wishlistPage);
